import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Shield, Lock, Eye, Database, UserCheck, AlertCircle } from 'lucide-react';
import { legalAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';

const PrivacyPolicyPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  // Determine userType from URL param, user role, or default to customer
  const urlUserType = searchParams.get('userType');
  const userType = (urlUserType || (user?.role === 'provider' ? 'driver' : 'customer')) as 'driver' | 'customer';
  const [content, setContent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLegalDocument();
  }, [userType, i18n.language]);

  const fetchLegalDocument = async () => {
    try {
      setLoading(true);
      const language = i18n.language || 'en';
      console.log(`Fetching privacy policy for userType: ${userType}, language: ${language}`);
      const response = await legalAPI.getLegalDocument('privacy', userType, language);
      if (response.success && response.data) {
        console.log(`Privacy policy fetched successfully. Content length: ${response.data.content?.length || 0}`);
        setContent(response.data.content);
      } else {
        setContent(null);
      }
    } catch (err: any) {
      console.error('Error fetching privacy policy:', err);
      setError(err.message);
      setContent(null);
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="flex items-center mb-4">
            <Shield className="h-8 w-8 text-black mr-3" />
            <h1 className="text-3xl font-bold text-black">{t('legal.privacyPolicy')}</h1>
          </div>
          <p className="text-gray-500">{t('legal.lastUpdated')}: {new Date().toLocaleDateString()}</p>
          <p className="text-gray-700 mt-4 leading-relaxed">
            {userType === 'driver' 
              ? t('legal.privacyIntroDriver')
              : t('legal.privacyIntro')}
          </p>
        </div>

        {/* Content */}
        {loading ? (
          <div className="bg-white rounded-lg shadow-sm p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto"></div>
            <p className="mt-4 text-gray-500">{t('legal.loadingPrivacy')}</p>
          </div>
        ) : error && !content ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <p className="text-red-800 font-medium">{t('legal.failedToLoad')} {t('legal.privacyPolicy').toLowerCase()}</p>
            <p className="text-red-600 text-sm mt-2">{error}</p>
          </div>
        ) : content ? (
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="markdown-content">
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-black mt-8 mb-4 pb-2 border-b border-gray-200" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-black mt-6 mb-3" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-black mt-4 mb-2" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-700 leading-relaxed mb-4" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc list-inside text-gray-700 my-4 space-y-2 ml-4" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal list-inside text-gray-700 my-4 space-y-2 ml-4" {...props} />,
                  li: ({node, ...props}) => <li className="text-gray-700 mb-2" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-black" {...props} />,
                  em: ({node, ...props}) => <em className="italic text-gray-700" {...props} />,
                  code: ({node, ...props}) => <code className="bg-gray-100 text-gray-800 px-1 py-0.5 rounded text-sm" {...props} />,
                  blockquote: ({node, ...props}) => <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4" {...props} />,
                  a: ({node, ...props}) => <a className="text-black underline hover:text-gray-600" {...props} />,
                }}
              >
                {content}
              </ReactMarkdown>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
          {/* Section 1 */}
          <section>
            <div className="flex items-center mb-4">
              <Database className="h-6 w-6 text-black mr-3" />
              <h2 className="text-2xl font-bold text-white">1. Information We Collect</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you register or use our services, we may collect:
              </p>
              <ul className="space-y-2 ml-6 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Name, email address, phone number</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Physical address and location data</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Payment information (processed securely through third-party payment processors)</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Profile information, including profile pictures and preferences</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>KYC (Know Your Customer) verification documents</span>
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">Booking and Service Information</h3>
              <ul className="space-y-2 ml-6 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Booking details, service requests, and transaction history</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Communication between customers and service providers</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Reviews, ratings, and feedback</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Complaints and support inquiries</span>
                </li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-800 mt-6 mb-2">Automated Information</h3>
              <ul className="space-y-2 ml-6 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>IP address, browser type, and device information</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Usage data, including pages visited and features used</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Cookies and similar tracking technologies</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Real-time location data (for driver services, with your consent)</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 2 */}
          <section>
            <div className="flex items-center mb-4">
              <Eye className="h-6 w-6 text-black mr-3" />
              <h2 className="text-2xl font-bold text-white">2. How We Use Your Information</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the collected information for the following purposes:
              </p>
              <ul className="space-y-2 ml-6 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Service Provision:</strong> Facilitating bookings, connecting customers with service providers</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Platform Improvement:</strong> Analyzing usage patterns to enhance user experience</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Communication:</strong> Sending notifications, updates, and promotional materials</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Safety and Security:</strong> Detecting and preventing fraud, abuse, and unauthorized access</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Customer Support:</strong> Responding to inquiries and resolving complaints</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Legal Compliance:</strong> Meeting regulatory requirements and legal obligations</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Analytics:</strong> Understanding user behavior and platform performance</span>
                </li>
              </ul>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <div className="flex items-center mb-4">
              <UserCheck className="h-6 w-6 text-black mr-3" />
              <h2 className="text-2xl font-bold text-white">3. Information Sharing and Disclosure</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                We may share your information with:
              </p>
              <ul className="space-y-3 ml-6 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>Service Providers:</strong> When you book a service, we share necessary information 
                    (name, contact details, booking details) with the assigned service provider to enable service delivery.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>Third-Party Service Providers:</strong> Payment processors, cloud hosting services, 
                    analytics providers, and other vendors who assist in operating our platform.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>Legal Authorities:</strong> When required by law, court order, or to protect our legal rights 
                    and the safety of our users.
                  </div>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <div>
                    <strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales.
                  </div>
                </li>
              </ul>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mt-4">
                <p className="text-blue-800 text-sm">
                  <strong>Note:</strong> We do not sell your personal information to third parties for their marketing purposes.
                </p>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <div className="flex items-center mb-4">
              <Lock className="h-6 w-6 text-black mr-3" />
              <h2 className="text-2xl font-bold text-white">4. Data Security</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              <ul className="space-y-2 ml-6 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Encryption of data in transit and at rest</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Secure authentication and authorization mechanisms</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Regular security audits and vulnerability assessments</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Limited access to personal information by authorized personnel only</span>
                </li>
              </ul>

              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mt-4">
                <div className="flex items-start">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p className="text-yellow-800 text-sm">
                    <strong>Important:</strong> While we strive to protect your information, no method of transmission 
                    over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <div className="flex items-center mb-4">
              <Shield className="h-6 w-6 text-black mr-3" />
              <h2 className="text-2xl font-bold text-white">5. Your Privacy Rights</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                You have the following rights regarding your personal information:
              </p>
              <ul className="space-y-2 ml-6 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Access:</strong> Request a copy of the personal information we hold about you</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Correction:</strong> Request correction of inaccurate or incomplete information</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Deletion:</strong> Request deletion of your personal information (subject to legal obligations)</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Data Portability:</strong> Request transfer of your data to another service</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span><strong>Object to Processing:</strong> Object to certain types of data processing</span>
                </li>
              </ul>
              
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, please contact us at <strong>privacy@allavail.com</strong>.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section>
            <div className="flex items-center mb-4">
              <Database className="h-6 w-6 text-black mr-3" />
              <h2 className="text-2xl font-bold text-white">6. Data Retention</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information for as long as necessary to fulfill the purposes outlined in this 
                Privacy Policy, unless a longer retention period is required by law. After the retention period, we will 
                securely delete or anonymize your information.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section>
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-black mr-3" />
              <h2 className="text-2xl font-bold text-white">7. Cookies and Tracking</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar technologies to:
              </p>
              <ul className="space-y-2 ml-6 text-gray-700">
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Remember your preferences and settings</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Analyze platform usage and performance</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Provide personalized content and recommendations</span>
                </li>
                <li className="flex items-start">
                  <span className="mr-2">•</span>
                  <span>Enable essential platform functionality</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can control cookies through your browser settings. Note that disabling cookies may affect platform functionality.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <div className="flex items-center mb-4">
              <UserCheck className="h-6 w-6 text-black mr-3" />
              <h2 className="text-2xl font-bold text-white">8. Children's Privacy</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed">
                Our platform is not intended for individuals under the age of 18. We do not knowingly collect personal 
                information from children. If you become aware that a child has provided us with personal information, 
                please contact us immediately.
              </p>
            </div>
          </section>

          {/* Section 9 */}
          <section>
            <div className="flex items-center mb-4">
              <Shield className="h-6 w-6 text-black mr-3" />
              <h2 className="text-2xl font-bold text-white">9. Changes to This Privacy Policy</h2>
            </div>
            <div className="prose prose-gray max-w-none">
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by 
                posting the new Privacy Policy on this page and updating the "Last Updated" date. Continued use of the 
                platform after changes constitute acceptance of the updated policy.
              </p>
            </div>
          </section>

          {/* Section 10 */}
          <section>
            <div className="flex items-center mb-4">
              <Lock className="h-6 w-6 text-black mr-3" />
              <h2 className="text-2xl font-bold text-white">10. Contact Us</h2>
            </div>
            <div className="bg-white rounded-lg p-6">
              <p className="text-gray-700 leading-relaxed">
                If you have any questions or concerns about this Privacy Policy or our data practices, please reach out via <strong>User &gt; Raise an Issue</strong>
              </p>
            </div>
          </section>

          {/* Consent */}
          <section className="border-t-2 border-gray-200 pt-6">
            <div className="bg-white rounded-lg p-6">
              <h3 className="text-lg font-semibold text-black mb-3">Your Consent</h3>
              <p className="text-gray-800 text-sm leading-relaxed">
                By using AllAvail, you consent to the collection, use, and disclosure of your information as described 
                in this Privacy Policy. If you do not agree with this policy, please discontinue use of our platform.
              </p>
            </div>
          </section>
        </div>
        )}
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;

